import { ArrowUpRight } from "lucide-react";

const STATS = [
  { kpi: "37%", label: "Avg. operating cost reduction", note: "Manufacturing client, 2024" },
  { kpi: "$1.2B", label: "Capital reallocated via models", note: "FI client, 2023" },
  { kpi: "11×", label: "Faster regulatory reporting", note: "Federal program, 2024" },
  { kpi: "99.97%", label: "Data pipeline SLA achieved", note: "Telecom, rolling 12mo" },
];

const CASES = [
  {
    tag: "B2G · Federal Civilian",
    title: "Audit-ready spend analytics for a 78,000-person agency",
    body:
      "We replaced 14 disconnected reporting workstreams with a single contract-grade data platform — clearing a GAO inquiry in 9 days instead of the projected 6 months.",
    metric: "9d audit close",
  },
  {
    tag: "B2B · Asset Management",
    title: "Portfolio risk surface for a $42B alternatives manager",
    body:
      "A nightly risk surface across 11 strategies that lets the CIO publish exposures by 6:30am and stress them on demand. Replaced a 9-analyst spreadsheet stack.",
    metric: "−63% reporting hours",
  },
  {
    tag: "B2B · Healthcare",
    title: "Bedside-to-board claims intelligence",
    body:
      "Unified claims, EHR, and pricing into a metric layer. Surfaced $48M of denied-claims recovery in the first quarter of operation.",
    metric: "$48M recovered",
  },
];

export default function StatsCases() {
  return (
    <section id="cases" data-testid="cases-section" className="border-b border-neutral-200 bg-white">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-20 md:py-32">
        <div className="flex items-center gap-4 mb-10">
          <span className="font-mono-tech text-xs tracking-[0.2em] uppercase text-neutral-500">
            [05] Outcomes & case studies
          </span>
          <span className="h-px flex-1 bg-neutral-200" />
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 border-l border-t border-neutral-200 mb-20">
          {STATS.map((s, i) => (
            <div
              key={i}
              data-testid={`stat-${i}`}
              className="border-r border-b border-neutral-200 p-6 md:p-8 flex flex-col gap-3 bg-white"
            >
              <span className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-500">
                /0{i + 1}
              </span>
              <div className="font-display text-5xl md:text-6xl font-black tracking-tighter">
                {s.kpi}
              </div>
              <div className="text-sm text-neutral-800 font-medium leading-snug">{s.label}</div>
              <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-500 mt-auto">
                {s.note}
              </div>
            </div>
          ))}
        </div>

        {/* Featured case + grid */}
        <div className="grid grid-cols-12 gap-0 border-t border-l border-neutral-200">
          <article
            data-testid="case-featured"
            className="col-span-12 lg:col-span-7 border-r border-b border-neutral-200 p-8 md:p-12 bg-neutral-950 text-white relative overflow-hidden"
          >
            <div className="absolute inset-y-0 left-0 w-[2px] bg-[#FF3300]" />
            <div className="flex items-center justify-between mb-10">
              <span className="font-mono-tech text-xs tracking-[0.2em] uppercase text-neutral-400">
                Featured engagement
              </span>
              <span className="font-mono-tech text-xs tracking-[0.2em] uppercase text-neutral-400">
                {CASES[0].tag}
              </span>
            </div>
            <h3 className="font-display text-3xl md:text-5xl font-black tracking-tighter leading-[1]">
              {CASES[0].title}
            </h3>
            <p className="mt-6 max-w-xl text-base md:text-lg text-neutral-300 leading-relaxed">
              {CASES[0].body}
            </p>
            <div className="mt-12 flex items-end justify-between">
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-400">
                  Headline outcome
                </div>
                <div className="font-display text-4xl md:text-5xl font-black mt-2">
                  {CASES[0].metric}
                </div>
              </div>
              <a
                href="#contact"
                data-testid="case-featured-cta"
                className="inline-flex items-center gap-2 text-sm font-semibold text-white border-b border-white pb-1 hover:text-[#FF3300] hover:border-[#FF3300] transition-colors"
              >
                Request the dossier <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </article>

          <div className="col-span-12 lg:col-span-5 grid grid-rows-2">
            {CASES.slice(1).map((c, i) => (
              <article
                key={i}
                data-testid={`case-${i + 1}`}
                className="border-r border-b border-neutral-200 p-8 md:p-10 bg-white hover:bg-neutral-50 transition-colors"
              >
                <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-4">
                  {c.tag}
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight leading-snug">
                  {c.title}
                </h3>
                <p className="mt-3 text-sm text-neutral-600 leading-relaxed line-clamp-3">{c.body}</p>
                <div className="mt-6 flex items-center justify-between">
                  <span className="font-display text-2xl font-black">{c.metric}</span>
                  <span className="font-mono-tech text-xs tracking-[0.2em] uppercase text-neutral-500">
                    Read →
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
